import type { Hit } from './platform';
export type IconName = 'undo' | 'redo' | 'eraser' | 'clear' | 'brush' | 'palette' | 'send' | 'download' | 'fill'
  | 'plane' | 'star' | 'heart' | 'mail' | 'frame' | 'gear' | 'copy' | 'share' | 'plus' | 'check' | 'refresh' | 'people' | 'shop'
  | 'play' | 'pause' | 'next' | 'warn';

/**
 * 「纸上邮局」设计令牌：暖纸 × 晴空蓝 × 朱砂 × 邮戳金。
 * 全部组件只引用这里的常量，页面里不散落色值；改色板只动这一处。
 */
export const C = {
  paper: '#F7F1E3', cream: '#FDF9EF', ink: '#2E3542', mute: '#8A8672', line: '#E4DBC6',
  accent: '#3E9BDC', accentDeep: '#2274B8', tint: '#E1F0FA',
  orange: '#E4572E', orangeDeep: '#C74522', peach: '#FBE3D2',
  white: '#FFFFFF', gold: '#D9A441', red: '#C2453C', blue: '#7FA6C9',
};

/**
 * 金色手写签名线（title 下方那道短划）的几何：标题基线下 size × drop 处起笔、线粗 width。
 * 它占掉标题下方的一整行——标题下面若要再排文字，必须预留这段高度，否则会被划掉
 * （v0.0.106 真机截图：页头「友友画」的品牌副标正好压在线上）。
 */
const SIGNATURE = { drop: 0.72, width: 2.4 };

/**
 * 字阶表：全套页面只允许这些按角色命名的字号，消灭散点字号。
 * 数值与既有视觉一一对应，改动只动这一处。
 */
export const T = {
  brand: 32,      // 欢迎页品牌大字
  display: 29,    // 欢迎页两行标语
  result: 26,     // 结果页揭晓标题
  logo: 24,       // 页头品牌「友友画」
  hero: 23,       // 区块大标题（今天画点什么 / 挑一个想画的词）
  title: 21,      // 页头斜排标题 / 词卡题目 / 等待页主标题
  big: 20,        // 空态主标题
  glyph: 19,      // 候选字块 / 答案格字符
  cardTitle: 18,  // 卡片标题（邀请码、昵称、作品题字）
  section: 17,    // 分组标题（友信 / 隐私条款标题）
  cardStrong: 16, // 卡片强调文案
  bodyStrong: 15, // 强调正文 / 卡片主文案
  body: 14,       // 正文 / 按钮 / 答案状态
  sub: 13,        // 副信息（会话 meta、商店价格）
  caption: 12,    // 辅助说明
  note: 11,       // 角注（版本号、分页）
  micro: 10.5,    // 导航标签 / 品牌副标
  tiny: 10,       // 照片墙手写字标注
} as const;


export class Painter {
  width = 390;
  tablet = false;
  x(value: number) { return 24 + (value - 24) * (this.width - 48) / 342; }
  span(value: number) { return value * (this.width - 48) / 342; }
  fontSize(size: number) { return this.tablet && size >= 10.5 ? size >= 20 ? Math.max(28, size) : Math.max(14, size + 2) : size; }

  hits: Hit[] = [];
  /** 当前被按住的命中区 id：按钮据此渲染按压回弹态。 */
  pressedId: string | null = null;
  private pattern: CanvasPattern | null = null;
  private textureSeed = 7;
  constructor(public ctx: CanvasRenderingContext2D) {}
  /** 全局纸纹：256px 离屏噪点 + 纤维丝，一次性生成、每帧平铺（确定性种子，离屏测试可复现）。 */
  setTexture(tile: HTMLCanvasElement) {
    const c = tile.getContext('2d')!;
    c.clearRect(0, 0, 256, 256);
    const rand = () => { this.textureSeed = (this.textureSeed * 16807) % 2147483647; return this.textureSeed / 2147483647; };
    for (let i = 0; i < 900; i++) {
      c.fillStyle = `rgba(46, 53, 66, ${0.02 + rand() * 0.05})`;
      c.fillRect(rand() * 256, rand() * 256, 1 + rand() * 1.2, 1 + rand() * 1.2);
    }
    c.strokeStyle = 'rgba(138, 134, 114, 0.05)'; c.lineWidth = 1;
    for (let i = 0; i < 26; i++) {
      const x = rand() * 256; const y = rand() * 256; const a = rand() * Math.PI; const l = 8 + rand() * 14;
      c.beginPath(); c.moveTo(x, y); c.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l); c.stroke();
    }
    this.pattern = this.ctx.createPattern(tile, 'repeat');
  }
  /** 每帧背景：纸色打底 + 纸纹平铺 + 顶部一抹柔光。 */
  background(w: number, h: number) {
    const c = this.ctx;
    c.fillStyle = C.paper; c.fillRect(0, 0, w, h);
    if (this.pattern) { c.save(); c.globalAlpha = 0.5; c.fillStyle = this.pattern; c.fillRect(0, 0, w, h); c.restore(); }
    const glow = c.createLinearGradient(0, 0, 0, h);
    glow.addColorStop(0, 'rgba(255,255,255,0.22)'); glow.addColorStop(0.3, 'rgba(255,255,255,0)');
    c.fillStyle = glow; c.fillRect(0, 0, w, h);
  }
  round(x: number, y: number, w: number, h: number, radius = 18) {
    const c = this.ctx; const r = Math.min(radius, w / 2, h / 2);
    c.beginPath(); c.moveTo(x + r, y); c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r); c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath();
  }
  box(x: number, y: number, w: number, h: number, fill: string | CanvasGradient = C.white, radius = 18, border?: string, shadow = 0) {
    const c = this.ctx;
    if (shadow) { c.save(); c.shadowColor = 'rgba(46, 53, 66, 0.10)'; c.shadowBlur = shadow; c.shadowOffsetY = Math.max(2, shadow / 4); }
    this.round(x, y, w, h, radius); c.fillStyle = fill; c.fill();
    if (shadow) c.restore();
    if (border) { c.strokeStyle = border; c.lineWidth = 1; c.stroke(); }
  }
  card(x: number, y: number, w: number, h: number, radius = 22) { this.box(x, y, w, h, C.cream, radius, undefined, 18); }
  text(text: string, x: number, y: number, size = 14, color = C.ink, weight = '400', align: CanvasTextAlign = 'left') {
    const c = this.ctx; c.fillStyle = color; c.font = `${weight} ${this.fontSize(size)}px "PingFang SC", "Microsoft YaHei", sans-serif`; c.textAlign = align; c.textBaseline = 'middle'; c.fillText(text, x, y);
  }
  wrap(text: string, x: number, y: number, width: number, size = 14, color = C.mute, gap = 23) {
    this.ctx.font = `400 ${this.fontSize(size)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
    let line = ''; let row = 0;
    for (const char of text) {
      if (char === '\n' || (this.ctx.measureText(line + char).width > width && !/[，。！？；：、.,!?;:）】》”’]/.test(char))) { this.text(line, x, y + row * gap, size, color); row++; line = char === '\n' ? '' : char; }
      else line += char;
    }
    if (line) this.text(line, x, y + row * gap, size, color);
    return y + (row + 1) * gap;
  }
  /** 拉丁微标逐字排版：全大写英文小标签加字距（如 DRAW A LITTLE CLOSER），中文不要用。 */
  tracking(text: string, x: number, y: number, size = 10, color = C.mute, weight = '600', gapPx = 1.5) {
    const c = this.ctx;
    c.font = `${weight} ${this.fontSize(size)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
    c.fillStyle = color; c.textAlign = 'left'; c.textBaseline = 'middle';
    let cx = x;
    for (const char of text) { c.fillText(char, cx, y); cx += c.measureText(char).width + gapPx; }
  }
  /** 限宽文字：超出 width 时省略号截断（真机字体比离屏宽，长昵称 / 长题目防顶到旁边的控件）。 */
  fit(text: string, x: number, y: number, width: number, size = 14, color = C.ink, weight = '400', align: CanvasTextAlign = 'left') {
    const c = this.ctx;
    c.font = `${weight} ${this.fontSize(size)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
    if (c.measureText(text).width > width) {
      let out = '';
      for (const char of text) { if (c.measureText(out + char + '…').width > width) break; out += char; }
      text = out + '…';
    }
    this.text(text, x, y, size, color, weight, align);
  }
  /** 缝线虚线：档案夹卡片、笔卷帘边缘的手工感分隔。 */
  dashed(x0: number, y0: number, x1: number, y1: number, color = C.mute, width = 1.4, dash: number[] = [4, 4]) {
    const c = this.ctx; c.save(); c.strokeStyle = color; c.lineWidth = width; c.lineCap = 'round'; c.setLineDash(dash);
    c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.stroke(); c.restore();
  }
  button(id: string, label: string, x: number, y: number, w: number, h: number, action: () => void, style: 'primary' | 'secondary' | 'plain' | 'orange' = 'primary', disabled = false, icon?: IconName, wechatProfile?: Hit['wechatProfile'], iconColor?: string) {
    const c = this.ctx; const radius = h / 2;
    const held = this.pressedId === id && !disabled;
    c.save();
    if (held) { c.translate(x + w / 2, y + h / 2); c.scale(0.95, 0.95); c.translate(-(x + w / 2), -(y + h / 2)); }
    if (!disabled && (style === 'primary' || style === 'orange')) {
      c.save();
      c.shadowColor = style === 'primary' ? 'rgba(34, 116, 184, 0.30)' : 'rgba(201, 71, 31, 0.30)';
      c.shadowBlur = held ? 6 : 12; c.shadowOffsetY = held ? 2 : 4;
      this.round(x, y, w, h, radius);
      const gradient = c.createLinearGradient(0, y, 0, y + h);
      // 晴空蓝 / 朱砂都压暗半档并向深木色收，纸面上更沉稳不“塑料”。
      if (style === 'primary') { gradient.addColorStop(0, '#4DA8E4'); gradient.addColorStop(1, '#1F6CAE'); }
      else { gradient.addColorStop(0, '#EE6B3F'); gradient.addColorStop(1, '#C9471F'); }
      c.fillStyle = gradient; c.fill(); c.restore();
      // 内侧顶部高光 + 一圈内描边：给渐变按钮“上釉”感；按压时随投影一起减弱。
      c.save();
      this.round(x + 1.5, y + 1.5, w - 3, h - 3, radius - 1.5);
      c.strokeStyle = `rgba(255, 255, 255, ${held ? 0.10 : 0.18})`; c.lineWidth = 1; c.stroke();
      c.clip();
      const sheen = c.createLinearGradient(0, y, 0, y + h * 0.55);
      sheen.addColorStop(0, `rgba(255, 255, 255, ${held ? 0.10 : 0.24})`); sheen.addColorStop(1, 'rgba(255, 255, 255, 0)');
      c.fillStyle = sheen; c.fillRect(x, y, w, h * 0.55); c.restore();
    } else {
      // 纸感按钮：secondary 是带晴空蓝描边的奶油纸签，plain 是白卡细边，disabled 是灰纸。
      const fill = disabled ? '#EFE8D8' : style === 'secondary' ? (held ? C.tint : C.cream) : C.white;
      const border = disabled ? undefined : style === 'secondary' ? 'rgba(34, 116, 184, 0.45)' : style === 'plain' ? '#DCD2BC' : undefined;
      this.box(x, y, w, h, fill, radius, border, disabled ? 0 : style === 'secondary' ? 5 : 3);
    }
    const labelColor = disabled ? '#A39E8A' : ['primary', 'orange'].includes(style) ? C.white : style === 'secondary' ? C.accentDeep : C.ink;
    const fontSize = h < 38 ? 12 : 14;
    if (icon) {
      const iconSize = Math.min(17, h * 0.45);
      c.font = `600 ${this.fontSize(fontSize)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
      const gap = label ? 5 : 0;
      const total = iconSize + gap + (label ? c.measureText(label).width : 0);
      const startX = x + Math.max(6, (w - total) / 2);
      // 图标默认跟随文字色；星星等需要固定品牌色的图标由 iconColor 覆盖（disabled 时一律灰）。
      this.icon(icon, startX + iconSize / 2, y + h / 2 + 0.5, iconSize, disabled ? labelColor : iconColor ?? labelColor);
      if (label) this.text(label, startX + iconSize + gap, y + h / 2 + 0.5, fontSize, labelColor, '600', 'left');
    } else this.text(label, x + w / 2, y + h / 2 + 0.5, fontSize, labelColor, '600', 'center');
    c.restore();
    this.hits.push({ id, label, x, y: this.tablet ? y - Math.max(0, 44 - h) / 2 : y, w, h: this.tablet ? Math.max(44, h) : h, disabled, action, ...(wechatProfile ? { wechatProfile } : {}) });
  }
  icon(name: IconName, cx: number, cy: number, size = 16, color = C.ink) {
    const c = this.ctx;
    c.save(); c.translate(cx, cy); c.scale(size / 24, size / 24);
    c.strokeStyle = color; c.fillStyle = color; c.lineWidth = 2.2; c.lineCap = 'round'; c.lineJoin = 'round';
    const stroke = (points: number[][]) => { c.beginPath(); points.forEach(([px, py], i) => i ? c.lineTo(px, py) : c.moveTo(px, py)); c.stroke(); };
    const dot = (x: number, y: number, r: number) => { c.beginPath(); c.arc(x, y, r, 0, Math.PI * 2); c.fill(); };
    switch (name) {
      case 'people':
        c.beginPath();c.arc(-3,-5,3.5,0,Math.PI*2);c.stroke();
        c.beginPath();c.arc(6,-3,2.7,-Math.PI*.6,Math.PI*.7);c.stroke();
        c.beginPath();c.moveTo(-10,8);c.bezierCurveTo(-10,-1,4,-1,4,8);c.closePath();c.stroke();
        c.beginPath();c.moveTo(6,2);c.quadraticCurveTo(11,3,11,8);c.lineTo(7,8);c.stroke();
        break;
      case 'shop':
        stroke([[-8,-2],[-8,9],[8,9],[8,-2]]);stroke([[-10,-3],[-7,-9],[7,-9],[10,-3]]);
        c.beginPath();c.moveTo(-10,-3);for(let x=-10;x<10;x+=5)c.quadraticCurveTo(x+2.5,2,x+5,-3);c.stroke();
        stroke([[-2,9],[-2,3],[4,3],[4,9]]);
        break;
      case 'undo':
        c.beginPath(); c.moveTo(9, 8); c.lineTo(9, -1); c.quadraticCurveTo(9, -4, 6, -4); c.lineTo(-3, -4); c.stroke();
        stroke([[2.2, -9.2], [-3, -4], [2.2, 1.2]]);
        break;
      case 'redo':
        c.beginPath(); c.moveTo(-9, 8); c.lineTo(-9, -1); c.quadraticCurveTo(-9, -4, -6, -4); c.lineTo(3, -4); c.stroke();
        stroke([[-2.2, -9.2], [3, -4], [-2.2, 1.2]]);
        break;
      case 'eraser':
        c.save(); c.rotate(-0.55);
        this.round(-8, -4.5, 15, 9, 2); c.stroke();
        stroke([[0.5, -4.5], [0.5, 4.5]]);
        c.restore();
        stroke([[-9.5, 8.5], [9.5, 8.5]]);
        break;
      case 'clear':
        stroke([[-7, -7], [7, -7]]); stroke([[-2.5, -9.2], [2.5, -9.2]]);
        this.round(-5, -5, 10, 13, 1.8); c.stroke();
        stroke([[-1.8, -1.5], [-1.8, 4.5]]); stroke([[1.8, -1.5], [1.8, 4.5]]);
        break;
      case 'brush':
        c.save(); c.rotate(-Math.PI / 4);
        this.round(-10, -3, 12.5, 6, 1.5); c.stroke();
        c.beginPath(); c.moveTo(2.5, -3); c.lineTo(9, 0); c.lineTo(2.5, 3); c.closePath(); c.stroke();
        dot(7.2, 0, 1.1);
        c.restore();
        break;
      case 'palette':
        c.beginPath(); c.arc(0, 0, 8, 0, Math.PI * 2); c.stroke();
        ([[-3.2, -3.2, '#E4572E'], [3.2, -3.6, '#D9A441'], [-3.4, 3.2, '#3E9BDC'], [3.6, 3.4, '#7FA6C9']] as const)
          .forEach(([dx, dy, fill]) => { c.fillStyle = fill; dot(dx, dy, 2.7); });
        break;
      case 'fill':
        // 油漆桶：倾斜的桶身 + 提手 + 侧面滴落的一滴颜料，代表“填满整张背景”。
        c.save(); c.rotate(-Math.PI / 8);
        c.beginPath(); c.moveTo(-7.2, -6.4); c.lineTo(4.4, -6.4); c.lineTo(7.2, 4.6); c.lineTo(-4.4, 4.6); c.closePath(); c.stroke();
        c.beginPath(); c.moveTo(-5.6, -6.4); c.quadraticCurveTo(0, -11.4, 5.6, -6.4); c.stroke();
        c.beginPath(); c.moveTo(-1.4, -6.4); c.lineTo(-1.4, 4.6); c.stroke();
        c.restore();
        c.beginPath(); c.moveTo(7.6, 1.4); c.quadraticCurveTo(10.6, 5.4, 7.6, 8.6); c.quadraticCurveTo(4.6, 5.4, 7.6, 1.4); c.fill();
        break;
      case 'send':
        c.beginPath(); c.moveTo(-9, 1.5); c.lineTo(9.5, -8); c.lineTo(2.5, 9); c.lineTo(-0.5, 2.8); c.closePath(); c.stroke();
        stroke([[9.5, -8], [-0.5, 2.8]]);
        break;
      case 'download':
        stroke([[0, -9.5], [0, 2.5]]); stroke([[-4.5, -1.5], [0, 3], [4.5, -1.5]]);
        c.beginPath(); c.moveTo(-8.5, 3.5); c.lineTo(-8.5, 8); c.quadraticCurveTo(-8.5, 9.5, -7, 9.5); c.lineTo(7, 9.5); c.quadraticCurveTo(8.5, 9.5, 8.5, 8); c.lineTo(8.5, 3.5); c.stroke();
        break;
      case 'plane':
        c.beginPath(); c.moveTo(10, -1); c.lineTo(-10, -7.5); c.lineTo(-3.5, 0.5); c.lineTo(-10, 7.5); c.closePath(); c.stroke();
        stroke([[-3.5, 0.5], [10, -1]]);
        break;
      case 'star':
        c.beginPath(); c.moveTo(0, -9);
        c.quadraticCurveTo(1.7, -1.7, 9, 0); c.quadraticCurveTo(1.7, 1.7, 0, 9); c.quadraticCurveTo(-1.7, 1.7, -9, 0); c.quadraticCurveTo(-1.7, -1.7, 0, -9);
        c.fill();
        break;
      case 'heart':
        c.beginPath(); c.moveTo(0, 8);
        c.bezierCurveTo(-11.5, 0.5, -8.5, -9.5, 0, -3.8); c.bezierCurveTo(8.5, -9.5, 11.5, 0.5, 0, 8);
        c.fill();
        break;
      case 'mail':
        this.round(-9, -6.5, 18, 13, 2); c.stroke();
        stroke([[-8.6, -5.2], [0, 1.5], [8.6, -5.2]]);
        break;
      case 'frame':
        this.round(-8.5, -7.5, 17, 15, 2); c.stroke();
        stroke([[-8.5, 4.2], [-2.8, -1.5], [1.8, 2.2], [5.2, -0.8], [8.5, 2.2]]);
        dot(3.6, -3.6, 1.5);
        break;
      case 'gear':
        c.beginPath(); c.arc(0, 0, 5.4, 0, Math.PI * 2); c.stroke();
        for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4; stroke([[Math.cos(a) * 5.4, Math.sin(a) * 5.4], [Math.cos(a) * 8.4, Math.sin(a) * 8.4]]); }
        c.beginPath(); c.arc(0, 0, 2.2, 0, Math.PI * 2); c.stroke();
        break;
      case 'copy':
        this.round(-7.5, -3.5, 12, 12, 2.5); c.stroke();
        c.beginPath(); c.moveTo(-2.5, -7.5); c.lineTo(5.5, -7.5); c.quadraticCurveTo(7.5, -7.5, 7.5, -5.5); c.lineTo(7.5, 5.5); c.stroke();
        break;
      case 'share':
        c.beginPath(); c.arc(-6, 0, 2.6, 0, Math.PI * 2); c.stroke();
        c.beginPath(); c.arc(6, -6, 2.6, 0, Math.PI * 2); c.stroke();
        c.beginPath(); c.arc(6, 6, 2.6, 0, Math.PI * 2); c.stroke();
        stroke([[-3.6, -1.2], [3.6, -4.8]]); stroke([[-3.6, 1.2], [3.6, 4.8]]);
        break;
      case 'plus':
        stroke([[0, -8], [0, 8]]); stroke([[-8, 0], [8, 0]]);
        break;
      case 'check':
        stroke([[-7, 0.5], [-2, 5.5], [7.5, -5]]);
        break;
      case 'refresh':
        // 环形箭头：约 300° 圆弧，箭头落在右侧上方、顺时钟指向右下（刷新 / 重播语义）。
        c.beginPath(); c.arc(0, 0, 7.6, Math.PI * 0.22, Math.PI * 1.89); c.stroke();
        stroke([[3.6, -3.4], [8.15, 0.3], [9.3, -5.4]]);
        break;
      case 'play':
        c.beginPath(); c.moveTo(-4, -7); c.lineTo(7.5, 0); c.lineTo(-4, 7); c.closePath(); c.fill();
        break;
      case 'pause':
        this.round(-6.5, -7, 4.4, 14, 1.4); c.fill();
        this.round(2.1, -7, 4.4, 14, 1.4); c.fill();
        break;
      case 'next':
        c.beginPath(); c.moveTo(-7.5, -6); c.lineTo(2.5, 0); c.lineTo(-7.5, 6); c.closePath(); c.fill();
        this.round(4.5, -6.5, 3.4, 13, 1.2); c.fill();
        break;
      case 'warn':
        // 警示三角：圆角三角描边 + 感叹号（线 + 点），只描不填；调用方传红色。
        c.beginPath(); c.moveTo(0, -8); c.lineTo(8.4, 6.4); c.lineTo(-8.4, 6.4); c.closePath(); c.stroke();
        stroke([[0, -2.6], [0, 1.8]]);
        dot(0, 4.6, 1.3);
        break;
    }
    c.restore();
  }
  iconButton(id: string, label: string, icon: IconName, cx: number, cy: number, r: number, action: () => void, style: 'secondary' | 'orange' | 'plain' = 'secondary', disabled = false) {
    const c = this.ctx;
    const held = this.pressedId === id && !disabled;
    const rr = held ? r * 0.95 : r;
    if (!disabled && style === 'orange') {
      c.save(); c.shadowColor = 'rgba(201, 71, 31, 0.32)'; c.shadowBlur = held ? 5 : 10; c.shadowOffsetY = held ? 1 : 3;
      c.beginPath(); c.arc(cx, cy, rr, 0, Math.PI * 2);
      const gradient = c.createLinearGradient(0, cy - rr, 0, cy + rr);
      gradient.addColorStop(0, '#EE6B3F'); gradient.addColorStop(1, '#C9471F');
      c.fillStyle = gradient; c.fill(); c.restore();
    } else {
      this.circle(cx, cy, rr, disabled ? '#EFE8D8' : style === 'secondary' ? (held ? C.tint : C.cream) : C.white,
        disabled ? undefined : style === 'secondary' ? 'rgba(34, 116, 184, 0.30)' : style === 'plain' ? '#DCD2BC' : undefined, disabled ? 0 : held ? 2 : 5);
    }
    this.icon(icon, cx, cy + 0.5, Math.min(19, rr * 0.86), disabled ? C.mute : style === 'orange' ? C.white : style === 'secondary' ? C.accentDeep : C.ink);
    const touch = this.tablet ? Math.max(44, r * 2 + 6) : r * 2 + 6;
    this.hits.push({ id, label, x: cx - touch / 2, y: cy - touch / 2, w: touch, h: touch, disabled, action });
  }
  circle(x: number, y: number, r: number, color: string, border?: string, shadow = 0) {
    const c = this.ctx;
    if (shadow) { c.save(); c.shadowColor = 'rgba(46, 53, 66, 0.22)'; c.shadowBlur = shadow; c.shadowOffsetY = 2; }
    c.beginPath(); c.arc(x, y, r, 0, Math.PI * 2); c.fillStyle = color; c.fill();
    if (shadow) c.restore();
    if (border) { c.strokeStyle = border; c.lineWidth = 2.5; c.stroke(); }
  }
  line(points: number[][], color = C.ink, width = 3) {
    const c = this.ctx; c.beginPath(); c.strokeStyle = color; c.lineWidth = width; c.lineCap = 'round'; c.lineJoin = 'round';
    points.forEach(([x, y], i) => i ? c.lineTo(x, y) : c.moveTo(x, y)); c.stroke();
  }
  /** 斜排手写感标题：轻微 -1.3° 旋转 + 金色签名 flourish（短划 + 末端圆点），全套页面的统一签名。
   *  签名线画在标题基线下 size × SIGNATURE.drop 处，占掉标题下方一整行（见 SIGNATURE 的说明）。 */
  title(text: string, x: number, y: number, size: number = T.title, color = C.ink) {
    const c = this.ctx; c.save(); c.translate(x, y); c.rotate(-0.023);
    this.text(text, 0, 0, size, color, '700');
    c.font = `700 ${this.fontSize(size)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
    const w = c.measureText(text).width;
    const dashEnd = Math.min(w * 0.58, w - 6);
    this.line([[3, this.fontSize(size) * 0.68], [dashEnd, this.fontSize(size) * SIGNATURE.drop]], C.gold, SIGNATURE.width);
    this.circle(dashEnd + 6.5, this.fontSize(size) * 0.66, 1.8, C.gold);
    c.restore();
  }
  /** 和纸胶带：半透明小纸条 + 撕边，贴在画纸、照片与词卡角上。 */
  tape(cx: number, cy: number, angle = 0, w = 52, h = 15) {
    const c = this.ctx; c.save(); c.translate(cx, cy); c.rotate(angle);
    c.save(); c.shadowColor = 'rgba(46, 53, 66, 0.12)'; c.shadowBlur = 3; c.shadowOffsetY = 1;
    c.fillStyle = 'rgba(236, 231, 190, 0.88)';
    c.beginPath();
    c.moveTo(-w / 2 + 3, -h / 2); c.lineTo(w / 2 - 3, -h / 2);
    c.lineTo(w / 2, -h / 2 + 3); c.lineTo(w / 2 - 2, 0); c.lineTo(w / 2, h / 2 - 3); c.lineTo(w / 2 - 3, h / 2);
    c.lineTo(-w / 2 + 3, h / 2); c.lineTo(-w / 2, h / 2 - 3); c.lineTo(-w / 2 + 2, 0); c.lineTo(-w / 2, -h / 2 + 3);
    c.closePath(); c.fill(); c.restore();
    c.strokeStyle = 'rgba(138, 134, 114, 0.4)'; c.lineWidth = 1;
    c.beginPath(); c.moveTo(-w / 2 + 7, 0); c.lineTo(w / 2 - 7, 0); c.stroke();
    c.restore();
  }
  /** 齿孔邮票：白底方票 + 四边纸色打孔 + 内框线；内容（头像 / 色格 / 面值）由调用方画进内框。 */
  stamp(x: number, y: number, w: number, h: number) {
    const c = this.ctx;
    this.box(x, y, w, h, C.white, 2.5, undefined, 6);
    c.fillStyle = C.paper;
    const step = 7; const r = 2.1;
    for (let px = x + 4; px <= x + w - 3; px += step) { c.beginPath(); c.arc(px, y, r, 0, Math.PI * 2); c.fill(); c.beginPath(); c.arc(px, y + h, r, 0, Math.PI * 2); c.fill(); }
    for (let py = y + 4; py <= y + h - 3; py += step) { c.beginPath(); c.arc(x, py, r, 0, Math.PI * 2); c.fill(); c.beginPath(); c.arc(x + w, py, r, 0, Math.PI * 2); c.fill(); }
    c.strokeStyle = C.line; c.lineWidth = 1; c.strokeRect(x + 5.5, y + 5.5, w - 11, h - 11);
  }
  /** 火漆印：不规则边缘的朱砂圆 + 高光 + 内凹小星星；盖在“轮到猜”的信封上。 */
  seal(cx: number, cy: number, r: number) {
    const c = this.ctx; c.save(); c.translate(cx, cy);
    c.beginPath();
    const n = 14;
    for (let i = 0; i <= n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = r * (1 + 0.07 * Math.sin(i * 2.7) + 0.05 * Math.cos(i * 1.3 + 1));
      const px = Math.cos(a) * rr; const py = Math.sin(a) * rr;
      i ? c.lineTo(px, py) : c.moveTo(px, py);
    }
    c.closePath();
    const g = c.createRadialGradient(-r * 0.3, -r * 0.35, r * 0.15, 0, 0, r * 1.25);
    g.addColorStop(0, '#F2794F'); g.addColorStop(1, '#C74522');
    c.save(); c.shadowColor = 'rgba(199, 69, 34, 0.38)'; c.shadowBlur = 6; c.shadowOffsetY = 2; c.fillStyle = g; c.fill(); c.restore();
    c.strokeStyle = 'rgba(255, 255, 255, 0.4)'; c.lineWidth = 1.3;
    c.beginPath(); c.arc(0, 0, r * 0.62, 0, Math.PI * 2); c.stroke();
    this.icon('star', 0, 0.3, r * 0.72, 'rgba(255, 255, 255, 0.85)');
    c.restore();
  }
  /** 圆形邮戳：双环 + 中间文字，半透明墨色、微旋转，像真的盖在信上。 */
  postmark(cx: number, cy: number, r: number, line1 = '', line2 = '', color = C.ink) {
    const c = this.ctx; c.save(); c.translate(cx, cy); c.rotate(-0.14); c.globalAlpha = 0.6;
    c.strokeStyle = color; c.lineWidth = 1.6;
    c.beginPath(); c.arc(0, 0, r, 0, Math.PI * 2); c.stroke();
    c.lineWidth = 1; c.beginPath(); c.arc(0, 0, r * 0.7, 0, Math.PI * 2); c.stroke();
    if (line1) this.text(line1, 0, line2 ? -r * 0.16 : 0, Math.max(9, r * 0.32), color, '700', 'center');
    if (line2) this.text(line2, 0, r * 0.3, Math.max(8, r * 0.24), color, '400', 'center');
    c.restore();
  }
  /** 纸飞机：寄信动效、等待页邮差与背景环境机共用；angle 为飞行方向。 */
  plane(cx: number, cy: number, angle = 0, scale = 1, color = C.ink) {
    const c = this.ctx; c.save(); c.translate(cx, cy); c.rotate(angle); c.scale(scale, scale);
    c.strokeStyle = color; c.lineWidth = 2; c.lineCap = 'round'; c.lineJoin = 'round';
    c.beginPath(); c.moveTo(12, 0); c.lineTo(-10, -7); c.lineTo(-3.5, 0); c.lineTo(-10, 7); c.closePath(); c.stroke();
    c.beginPath(); c.moveTo(-3.5, 0); c.lineTo(12, 0); c.stroke();
    c.restore();
  }
  /** 四角小星星：星星货币、闪光与彩纸共用。 */
  star(cx: number, cy: number, r: number, color = C.gold) {
    const c = this.ctx; c.save(); c.translate(cx, cy);
    c.beginPath(); c.moveTo(0, -r);
    c.quadraticCurveTo(r * 0.19, -r * 0.19, r, 0); c.quadraticCurveTo(r * 0.19, r * 0.19, 0, r);
    c.quadraticCurveTo(-r * 0.19, r * 0.19, -r, 0); c.quadraticCurveTo(-r * 0.19, -r * 0.19, 0, -r);
    c.fillStyle = color; c.fill(); c.restore();
  }
  /** 颜料滴：微微不规则的色点 + 高光；选中态加白纸环与投影。 */
  dab(cx: number, cy: number, r: number, color: string, active = false) {
    const c = this.ctx;
    c.beginPath();
    const n = 10;
    for (let i = 0; i <= n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = r * (1 + 0.06 * Math.sin(i * 2.3 + cx * 0.13));
      const px = cx + Math.cos(a) * rr; const py = cy + Math.sin(a) * rr;
      i ? c.lineTo(px, py) : c.moveTo(px, py);
    }
    c.closePath();
    if (active) { c.save(); c.shadowColor = 'rgba(46, 53, 66, 0.32)'; c.shadowBlur = 7; c.shadowOffsetY = 2; }
    c.fillStyle = color; c.fill();
    if (active) c.restore();
    c.strokeStyle = active ? C.white : C.line; c.lineWidth = active ? 2.5 : 1; c.stroke();
    c.beginPath(); c.arc(cx - r * 0.3, cy - r * 0.35, Math.max(1.4, r * 0.17), 0, Math.PI * 2);
    c.fillStyle = 'rgba(255, 255, 255, 0.45)'; c.fill();
  }
  /** 博物馆画框：木框 + 金线 + 斜投影；只画框环（evenodd 掏空中部），画作由调用方画在内框 (x,y,w,h)。 */
  frame(x: number, y: number, w: number, h: number, angle = 0) {
    const c = this.ctx; c.save(); c.translate(x + w / 2, y + h / 2); c.rotate(angle); c.translate(-w / 2, -h / 2);
    c.save(); c.shadowColor = 'rgba(46, 53, 66, 0.2)'; c.shadowBlur = 10; c.shadowOffsetY = 4;
    c.beginPath(); c.rect(-8, -8, w + 16, h + 16); c.rect(-6, -6, w + 12, h + 12);
    c.fillStyle = '#6F5238'; c.fill('evenodd'); c.restore();
    c.beginPath(); c.rect(-6, -6, w + 12, h + 12); c.rect(-1.5, -1.5, w + 3, h + 3);
    c.fillStyle = '#96754F'; c.fill('evenodd');
    c.strokeStyle = C.gold; c.lineWidth = 1.6; c.strokeRect(-2.5, -2.5, w + 5, h + 5);
    c.restore();
  }
  /** 信封插画：letter = 信纸探出比例（0~1），seal = 是否带火漆。 */
  envelope(cx: number, cy: number, w: number, h: number, opts: { seal?: boolean; letter?: number } = {}) {
    const c = this.ctx; const x = cx - w / 2; const y = cy - h / 2;
    if (opts.letter) {
      const ly = y - opts.letter * h * 0.42;
      this.box(x + w * 0.09, ly, w * 0.82, h * 0.92, C.white, 4, C.line, 8);
      c.save(); c.globalAlpha = 0.55;
      this.line([[x + w * 0.2, ly + h * 0.26], [x + w * 0.8, ly + h * 0.26]], C.line, 2);
      this.line([[x + w * 0.2, ly + h * 0.46], [x + w * 0.66, ly + h * 0.46]], C.line, 2);
      this.line([[x + w * 0.2, ly + h * 0.66], [x + w * 0.74, ly + h * 0.66]], C.line, 2);
      c.restore();
    }
    this.box(x, y, w, h, C.cream, 8, undefined, 12);
    this.line([[x + 2, y + 4], [cx, y + h * 0.54], [x + w - 2, y + 4]], '#D8CBB0', 2);
    this.line([[x + 2, y + h - 4], [cx - w * 0.17, cy + h * 0.1]], '#E0D4BA', 1.6);
    this.line([[x + w - 2, y + h - 4], [cx + w * 0.17, cy + h * 0.1]], '#E0D4BA', 1.6);
    if (opts.seal) this.seal(cx, y + h * 0.54, Math.min(14, h * 0.2));
  }
  /** 邮筒插画：等待页的主角，配纸飞机航线动画。 */
  mailbox(cx: number, cy: number, s = 1) {
    const c = this.ctx; c.save(); c.translate(cx, cy); c.scale(s, s);
    this.box(-5, 18, 10, 36, '#8A6B4A', 3);
    c.beginPath(); c.moveTo(-34, 20); c.lineTo(-34, -6); c.arc(0, -6, 34, Math.PI, 0); c.lineTo(34, 20); c.closePath();
    const g = c.createLinearGradient(0, -40, 0, 20); g.addColorStop(0, '#EE6B3F'); g.addColorStop(1, '#C9471F');
    c.save(); c.shadowColor = 'rgba(201, 71, 31, 0.3)'; c.shadowBlur = 10; c.shadowOffsetY = 4; c.fillStyle = g; c.fill(); c.restore();
    this.box(-24, -17, 48, 7, '#A83A18', 3.5);
    c.strokeStyle = 'rgba(255, 255, 255, 0.5)'; c.lineWidth = 1.6;
    c.beginPath(); c.moveTo(-28, 20); c.lineTo(-28, -6); c.arc(0, -6, 28, Math.PI, 0); c.lineTo(28, 20); c.closePath(); c.stroke();
    this.box(22, -36, 4, 24, C.orange, 2); this.box(22, -36, 16, 10, C.orange, 2);
    c.restore();
  }
  doodle(x: number, y: number, scale = 1) {
    const c = this.ctx; c.save(); c.translate(x, y); c.scale(scale, scale);
    this.box(-62, -62, 125, 125, C.cream, 30, undefined, 14);
    this.line([[-34, 5], [-37, -35], [-14, -23], [14, -23], [37, -35], [34, 5], [24, 27], [0, 35], [-24, 27], [-34, 5]], C.ink, 3);
    this.circle(-15, 0, 3, C.ink); this.circle(15, 0, 3, C.ink); this.circle(-23, 10, 7, '#F3B49A'); this.circle(23, 10, 7, '#F3B49A');
    this.line([[-4, 10], [0, 14], [4, 10]], C.ink, 2); this.line([[0, 14], [0, 21], [-5, 24]], C.ink, 2); this.line([[0, 21], [5, 24]], C.ink, 2);
    this.line([[-30, 12], [-49, 9]], C.ink, 2); this.line([[30, 12], [49, 9]], C.ink, 2);
    c.restore();
  }
}
